import React from 'react'
import { ColDef, GridReadyEvent, ICellRendererParams } from 'ag-grid-community'
import { Link, Text } from '@chakra-ui/react'

import SetFilter from 'components/AgGrid/filters/SetFilter'
import { BaseGrid } from 'components/TailwindUIToolkit'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { EvictionsQuery } from 'graphql/generated'
import { formatUTCDate } from 'lib/date-time'
import { formatMoney } from 'lib/numbers'

export type Eviction = EvictionsQuery['evictions'][number]

const formatDate = (val: string | null | undefined) => formatUTCDate(val, 'MM/dd/yyyy')

const adminName = (admin: { first_name: string; last_name: string } | null | undefined) => {
  if (!admin) return ''
  return `${admin.first_name} ${admin.last_name}`
}

const filingFeeTotal = (eviction: Eviction | undefined) =>
  (eviction?.eviction_filing_fees ?? []).reduce(
    (total, filingFee) => total + Number(filingFee.rental_liability.amount),
    0
  )

export const getColDefs = (setActiveEviction: (e: Eviction) => void): ColDef<Eviction>[] => [
  {
    headerName: 'Address',
    colId: 'address',
    valueGetter: (params) =>
      params.data?.rental?.portfolio_home?.home?.address?.display_line_1 ?? '',
    cellRenderer: (params: ICellRendererParams<Eviction>) => {
      const address = params.data?.rental?.portfolio_home?.home?.address?.display_line_1
      const address2 = params.data?.rental?.portfolio_home?.home?.address?.display_line_2
      const rentalId = params.data?.rental?.id
      if (!address || !rentalId) return null
      return (
        <div className='flex flex-col'>
          <Link href={`/rent-roll/${rentalId}`} color='teal.500' target='_blank' rel='noreferrer'>
            {address}
          </Link>
          {address2 && (
            <Text fontSize='xs' color='gray.500'>
              {address2}
            </Text>
          )}
        </div>
      )
    },
    minWidth: 220,
    sort: 'asc',
    pinned: 'left'
  },
  {
    headerName: 'Tenant',
    colId: 'tenant',
    valueGetter: (params) => {
      const user = params.data?.rental?.user
      if (!user) return ''
      return `${user.first_name} ${user.last_name}`
    },
    minWidth: 150
  },
  {
    headerName: 'Fund',
    colId: 'fund',
    filter: SetFilter,
    valueGetter: (params) => {
      const fundLlcs = params.data?.rental?.portfolio_home?.most_recent_llc_property?.llc?.fund_llcs
      return fundLlcs?.[0]?.fund?.name ?? ''
    },
    cellRenderer: (params: ICellRendererParams<Eviction, string>) =>
      params.value ? mapContentToBadge(params.value, { size: 'xs' }) : null,
    minWidth: 100
  },
  {
    headerName: 'Consent Judgement',
    field: 'consent_judgement',
    cellRenderer: (params: ICellRendererParams<Eviction>) => {
      if (!params.data?.consent_judgement) return null
      return mapContentToBadge('Yes', { colorOverride: 'Purple', size: 'xs' })
    },
    minWidth: 140
  },
  {
    headerName: 'Total Fees Charged',
    colId: 'filing_fee_total',
    valueGetter: (params) => filingFeeTotal(params.data),
    valueFormatter: (params) => formatMoney(Number(params.value ?? 0), 2, '$'),
    minWidth: 140
  },
  {
    headerName: 'Latest Note',
    colId: 'latest_note',
    valueGetter: (params) => params.data?.rental?.notes?.[0]?.note ?? '',
    minWidth: 200
  },
  {
    headerName: 'Expand Notes',
    colId: 'expand_notes',
    cellRenderer: (params: ICellRendererParams<Eviction>) => {
      if (!params.data) return null
      return (
        <button
          onClick={() => setActiveEviction(params.data!)}
          className='text-teal-500 underline hover:text-teal-600'
        >
          View
        </button>
      )
    },
    minWidth: 100,
    sortable: false,
    filter: false
  },
  {
    headerName: 'Status',
    field: 'status',
    filter: SetFilter,
    cellRenderer: (params: ICellRendererParams<Eviction>) => {
      const status = params.data?.status
      if (!status) return null
      return mapContentToBadge(status, { size: 'xs' })
    },
    minWidth: 110
  },
  {
    headerName: 'Date Created',
    field: 'created_at',
    valueFormatter: (params) => formatDate(params.value),
    minWidth: 120
  },
  {
    headerName: 'Date Last Updated',
    field: 'updated_at',
    valueFormatter: (params) => formatDate(params.value),
    minWidth: 140
  },
  {
    headerName: 'Court Date',
    field: 'court_date',
    valueFormatter: (params) => formatDate(params.value),
    minWidth: 110
  },
  {
    headerName: 'Set Out Date',
    field: 'set_out_date',
    valueFormatter: (params) => formatDate(params.value),
    minWidth: 120
  },
  {
    headerName: 'Date Eviction Completed',
    field: 'completed_at',
    valueFormatter: (params) => formatDate(params.value),
    minWidth: 170
  },
  {
    headerName: 'Completed By Admin',
    colId: 'completed_by',
    valueGetter: (params) => adminName(params.data?.completed_by_admin),
    minWidth: 150
  },
  {
    headerName: 'Date Eviction Canceled',
    field: 'canceled_at',
    valueFormatter: (params) => formatDate(params.value),
    minWidth: 170
  },
  {
    headerName: 'Canceled By Admin',
    colId: 'canceled_by',
    valueGetter: (params) => adminName(params.data?.canceled_by_admin),
    minWidth: 150
  },
  {
    headerName: 'Cancelation Reason',
    field: 'cancelation_reason',
    filter: SetFilter,
    minWidth: 180
  }
]

interface GridProps {
  data: Eviction[]
  onGridReady: (evt: GridReadyEvent<Eviction>) => void
  setActiveEviction: (e: Eviction) => void
}

const EvictionsGrid: React.FC<GridProps> = ({ data, onGridReady, setActiveEviction }) => {
  const columns = getColDefs(setActiveEviction)

  return (
    <div style={{ height: '74vh' }}>
      <BaseGrid<Eviction>
        rowData={data}
        columns={columns}
        optionOverrides={{
          pagination: true,
          paginationPageSize: 20
        }}
        defaultColDefOverrides={{
          floatingFilter: true,
          resizable: true,
          wrapText: false,
          minWidth: 100
        }}
        rowAutoHeight={true}
        onGridReady={onGridReady}
        onRowDoubleClicked={(evt) => {
          if (evt.data) setActiveEviction(evt.data)
        }}
      />
    </div>
  )
}

export default EvictionsGrid
